// useOwnerProfile — the signed-in operator's display identity.
//
// Single source of truth for the name/title/initials shown around the app
// (sidebar, dashboard greeting, settings), so nothing is hardcoded to one
// customer. Name comes from public.users.display_name (the owner can read
// their own row — migration 0002); the org/title subtitle is a per-deploy
// brand value (VITE_OWNER_TITLE), falling back to the role.

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';

export type OwnerProfile = {
  /** Full display name, e.g. "Joel Vierra". */
  name: string;
  /** First token of the name, for greetings. */
  firstName: string;
  /** Org / role subtitle, e.g. "Founder, Reverra Health" or "Owner". */
  title: string;
  /** Up-to-2-letter avatar initials. */
  initials: string;
  email: string | null;
  /** users.role for the signed-in session ('owner' | 'admin' | 'dev' | …); null until loaded. */
  role: string | null;
};

export function useOwnerProfile(): OwnerProfile {
  const { user } = useAuth();

  const profileQ = useQuery({
    queryKey: ['owner-profile', user?.id],
    enabled: Boolean(user?.id),
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data } = await supabase
        .from('users')
        .select('display_name, role')
        .eq('id', user!.id)
        .maybeSingle();
      return data as { display_name: string | null; role: string | null } | null;
    },
  });

  const name = profileQ.data?.display_name?.trim() || user?.email?.split('@')[0] || 'Operator';
  const firstName = name.split(/\s+/)[0] || name;
  const role = profileQ.data?.role;
  const title =
    (import.meta.env.VITE_OWNER_TITLE as string | undefined)?.trim() ||
    (role ? role[0].toUpperCase() + role.slice(1) : '');
  const initials =
    name
      .split(/\s+/)
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || 'R';

  return { name, firstName, title, initials, email: user?.email ?? null, role: role ?? null };
}
